import React from 'react';
import {
  Paper, Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, MenuItem, IconButton, Button,
  InputAdornment,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import {
  Environment, Disk, DatabaseType, DatabaseConfig, DatabaseEdition, Addon,
  DatabaseEditionPricingRow,
} from '../../types';
import {
  DatabaseRow, PaasDatabaseRow, VmDatabaseRow, SelectOption, NormalizedDefaultAddon,
  DISK_LIST_SX, HEADER_CELL_SX, TABLE_CELL_SX, SPACER_CELL_SX,
  COL, CELL_W, NO_SPIN_SX, rowErrorSX, BACKUP_OPTIONS,
} from './types';
import { formatCurrency, formatDiskSize } from '../../utils/formatCurrency';

interface DatabasesTableProps {
  databases: DatabaseRow[];
  environments: Environment[];
  filteredVMTypes: { CPU: number }[];
  filteredDisks: Disk[];
  disks: Disk[];
  databaseTypes: DatabaseType[];
  filteredDatabaseTypes: DatabaseType[];
  databaseEditions: DatabaseEdition[];
  filteredDatabaseConfigs: DatabaseConfig[];
  addons: Addon[];
  defaultAddons: NormalizedDefaultAddon[];
  databaseVMPricing: Record<string, DatabaseEditionPricingRow>;
  selectedRegion: number | string;
  pricingReady: boolean;
  // Handlers
  onUpdateDatabase: (id: number, field: string, value: unknown) => void;
  onDeleteDatabase: (id: number) => void;
  onAddDatabaseDisk: (dbId: number) => void;
  onUpdateDatabaseDisk: (dbId: number, idx: number, diskId: number | null) => void;
  onDeleteDatabaseDisk: (dbId: number, idx: number) => void;
  // Price/availability helpers
  isVmDbRowAvailable: (db: DatabaseRow) => boolean;
  isPaasDbRowAvailable: (db: DatabaseRow) => boolean;
  getVMTypeId: (cpu: number, ram: number) => number | undefined;
  getDatabaseVMPrice: (vmTypeId: number | undefined, editionId: number | string, regionId: number | string, dbDisks: (number | null)[], environmentId: number) => number;
  getDatabasePrice: (configId: number | string, regionId: number | string) => number;
  getAvailableRAMForDatabase: (cpu: number) => number[];
  getAvailableTiers: (databaseTypeId: number | string, cores: number) => DatabaseConfig[];
  ensureCurrentOption: (options: SelectOption[], currentValue: number | string | null | undefined, makeLabel: (v: number | string) => string) => SelectOption[];
}

const DatabasesTable: React.FC<DatabasesTableProps> = ({
  databases, environments, filteredVMTypes, filteredDisks, disks,
  databaseTypes, filteredDatabaseTypes, databaseEditions, filteredDatabaseConfigs,
  addons, defaultAddons, databaseVMPricing, selectedRegion, pricingReady,
  onUpdateDatabase, onDeleteDatabase, onAddDatabaseDisk, onUpdateDatabaseDisk, onDeleteDatabaseDisk,
  isVmDbRowAvailable, isPaasDbRowAvailable, getVMTypeId, getDatabaseVMPrice, getDatabasePrice,
  getAvailableRAMForDatabase, getAvailableTiers, ensureCurrentOption,
}) => {
  if (databases.length === 0) return null;

  return (
    <Paper sx={{ mb: 3, elevation: 2, border: 1, borderColor: 'grey.300' }}>
      <Box sx={{ p: 1, backgroundColor: 'grey.300', borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>Databases</Typography>
        {databases.some((db) => db.deploymentType === 'vm_based') && (
          <>
            {Array.from(new Set(databases.filter(db => db.deploymentType === 'vm_based').map(db => (db as VmDatabaseRow).environmentId ?? 1))).map(envId => {
              const env = environments.find(e => e.EnvironmentId === envId);
              const envAddons = defaultAddons
                .filter(da => da.EnvironmentId === envId && da.ApplyToDatabaseVMs)
                .map(da => addons.find(a => a.AddonId === da.AddonId)?.AddonName)
                .filter(Boolean)
                .join(', ');
              return (
                <Typography key={envId} variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  <strong>{env?.EnvironmentName} Database VMs include {env?.StandardStorageGB}GB standard storage{envAddons && ' and the following add-ons:'}</strong>
                  {envAddons && ` ${envAddons}`}
                </Typography>
              );
            })}
          </>
        )}
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...HEADER_CELL_SX, ...CELL_W(COL.NAME) }}>Component</TableCell>
              <TableCell sx={{ ...HEADER_CELL_SX, ...CELL_W(COL.ENV) }}>Env</TableCell>
              <TableCell sx={{ ...HEADER_CELL_SX, ...CELL_W(COL.CPU) }}>CPU</TableCell>
              <TableCell sx={{ ...HEADER_CELL_SX, ...CELL_W(COL.RAM) }}>RAM</TableCell>
              <TableCell sx={{ ...HEADER_CELL_SX, ...CELL_W(COL.OS) }}>Edition</TableCell>
              <TableCell sx={{ ...HEADER_CELL_SX, ...CELL_W(COL.BACKUPS), textAlign: 'center' }}>Daily Backups</TableCell>
              <TableCell sx={{ ...HEADER_CELL_SX, minWidth: COL.STORAGE, textAlign: 'center' }}>Extra Storage</TableCell>
              <TableCell sx={{ ...HEADER_CELL_SX, ...SPACER_CELL_SX }}></TableCell>
              <TableCell sx={{ ...HEADER_CELL_SX, ...CELL_W(COL.QTY), textAlign: 'center' }}>Qty</TableCell>
              <TableCell sx={{ ...HEADER_CELL_SX, ...CELL_W(COL.COST), textAlign: 'right' }}>Cost/mo</TableCell>
              <TableCell sx={{ ...HEADER_CELL_SX, ...CELL_W(COL.ACT) }} />
            </TableRow>
          </TableHead>

          <TableBody>
            {databases.map((db) => {
              const isVMDatabase = db.deploymentType === 'vm_based';
              const dbAvailable = isVMDatabase ? isVmDbRowAvailable(db) : isPaasDbRowAvailable(db);

              let cost = 0;
              if (!pricingReady && db.savedLineCost != null) {
                cost = db.savedLineCost;
              } else if (isVMDatabase && db.deploymentType === 'vm_based') {
                const vmTypeId = getVMTypeId(db.cpu, db.ram);
                cost = vmTypeId ? (getDatabaseVMPrice(vmTypeId, db.editionId, selectedRegion, db.disks ?? [], db.environmentId ?? 1) * db.quantity) : 0;
              } else if (db.deploymentType === 'paas') {
                cost = getDatabasePrice(db.databaseConfigId, selectedRegion) * db.quantity;
              }

              const dbTypeOptionsAvail: SelectOption[] = filteredDatabaseTypes.map((type) => ({ value: type.DatabaseTypeId, label: type.DatabaseName }));
              const dbTypeLabel = (val: number | string) => databaseTypes.find((t) => t.DatabaseTypeId === Number(val))?.DatabaseName ?? String(val);
              const dbTypeOptsWithCurrent = ensureCurrentOption(dbTypeOptionsAvail, db.databaseTypeId, dbTypeLabel);

              if (db.deploymentType === 'vm_based') {
                const cpuOptions: SelectOption[] = [...new Set(filteredVMTypes.map((v) => v.CPU))].sort((a, b) => a - b).map((cpu) => ({ value: cpu, label: cpu }));
                const cpuOptsWithCurrent = ensureCurrentOption(cpuOptions, db.cpu, (v) => String(v));

                const availRAM: SelectOption[] = getAvailableRAMForDatabase(db.cpu).map((ram) => ({ value: ram, label: ram }));
                const ramOptsWithCurrent = ensureCurrentOption(availRAM, db.ram, (v) => String(v));

                const editionAll = databaseEditions.filter((e) => e.DatabaseTypeId === db.databaseTypeId);
                const editionAvail: SelectOption[] = editionAll.filter((ed) =>
                  Object.keys(databaseVMPricing).some((key) => {
                    const [, edId, regId] = key.split('-');
                    return parseInt(edId!, 10) === ed.DatabaseEditionId &&
                           parseInt(regId!, 10) === parseInt(String(selectedRegion), 10) &&
                           databaseVMPricing[key]?.IsAvailable;
                  })
                ).map((ed) => ({ value: ed.DatabaseEditionId, label: ed.EditionName }));

                const edLabel = (val: number | string) => editionAll.find((e) => e.DatabaseEditionId === Number(val))?.EditionName ?? String(val);
                const edOptsWithCurrent = ensureCurrentOption(editionAvail, db.editionId, edLabel);

                return (
                  <TableRow key={db.id} hover sx={!dbAvailable ? rowErrorSX : undefined}>
                    <TableCell sx={{ ...TABLE_CELL_SX, ...CELL_W(COL.NAME) }}>
                      <TextField select size="small" value={db.databaseTypeId} onChange={(e) => onUpdateDatabase(db.id, 'databaseTypeId', e.target.value)} fullWidth>
                        {dbTypeOptsWithCurrent.map((opt) => <MenuItem key={`type-${opt.value}`} value={opt.value} disabled={opt.disabled}>{opt.label}</MenuItem>)}
                      </TextField>
                    </TableCell>
                    <TableCell sx={{ ...TABLE_CELL_SX, ...CELL_W(COL.ENV) }}>
                      <TextField select size="small" value={db.environmentId ?? environments[0]?.EnvironmentId ?? 1} onChange={(e) => onUpdateDatabase(db.id, 'environmentId', parseInt(e.target.value, 10))} fullWidth>
                        {environments.map((env) => (<MenuItem key={env.EnvironmentId} value={env.EnvironmentId}>{env.EnvironmentName}</MenuItem>))}
                      </TextField>
                    </TableCell>
                    <TableCell sx={{ ...TABLE_CELL_SX, ...CELL_W(COL.CPU) }}>
                      <TextField select size="small" value={db.cpu ?? ''} onChange={(e) => onUpdateDatabase(db.id, 'cpu', Number(e.target.value))} fullWidth>
                        {cpuOptsWithCurrent.map((opt) => <MenuItem key={`dbcpu-${opt.value}`} value={opt.value} disabled={opt.disabled}>{opt.label}</MenuItem>)}
                      </TextField>
                    </TableCell>
                    <TableCell sx={{ ...TABLE_CELL_SX, ...CELL_W(COL.RAM) }}>
                      <TextField select size="small" value={db.ram ?? ''} onChange={(e) => onUpdateDatabase(db.id, 'ram', Number(e.target.value))} fullWidth>
                        {ramOptsWithCurrent.map((opt) => <MenuItem key={`dbram-${opt.value}`} value={opt.value} disabled={opt.disabled}>{opt.label}</MenuItem>)}
                      </TextField>
                    </TableCell>
                    <TableCell sx={{ ...TABLE_CELL_SX, ...CELL_W(COL.OS) }}>
                      <TextField select size="small" value={db.editionId ?? ''} onChange={(e) => onUpdateDatabase(db.id, 'editionId', Number(e.target.value))} fullWidth>
                        {edOptsWithCurrent.map((opt) => <MenuItem key={`dbed-${opt.value}`} value={opt.value} disabled={opt.disabled}>{opt.label}</MenuItem>)}
                      </TextField>
                    </TableCell>
                    <TableCell sx={{ ...TABLE_CELL_SX, ...CELL_W(COL.BACKUPS), textAlign: 'center' }}>
                      <TextField select size="small" value={(db as VmDatabaseRow).dailyBackups ?? 0} onChange={(e) => onUpdateDatabase(db.id, 'dailyBackups', parseInt(e.target.value, 10))} sx={{ width: 80 }}>
                        {BACKUP_OPTIONS.map((opt) => <MenuItem key={`vbk-${opt.value}`} value={opt.value}>{opt.label}</MenuItem>)}
                      </TextField>
                    </TableCell>
                    <TableCell sx={{ ...TABLE_CELL_SX, minWidth: COL.STORAGE }}>
                      <Box sx={DISK_LIST_SX}>
                        {(db.disks ?? []).map((diskId, idx) => {
                          const diskOptions: SelectOption[] = filteredDisks.map((d) => ({ value: d.DiskId, label: formatDiskSize(d.DiskSize) }));
                          const diskLabel = (val: number | string) => { const d = disks.find((disk) => disk.DiskId === Number(val)); return d ? formatDiskSize(d.DiskSize) : String(val); };
                          const diskOptsWithCurrent = ensureCurrentOption(diskOptions, diskId ?? '', diskLabel);
                          return (
                            <Box key={idx} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                              <TextField select size="small" value={diskId ?? ''} onChange={(e) => onUpdateDatabaseDisk(db.id, idx, e.target.value ? Number(e.target.value) : null)} sx={{ width: 112 }}>
                                {diskOptsWithCurrent.length > 0
                                  ? diskOptsWithCurrent.map((opt) => <MenuItem key={`dbd-${idx}-${opt.value}`} value={opt.value} disabled={opt.disabled ?? false}>{opt.label}</MenuItem>)
                                  : <MenuItem value="">None</MenuItem>}
                              </TextField>
                              <IconButton size="small" onClick={() => onDeleteDatabaseDisk(db.id, idx)} sx={{ p: 0.25 }}><DeleteIcon sx={{ fontSize: '1rem' }} /></IconButton>
                            </Box>
                          );
                        })}
                        {(db.disks ?? []).length < 3 && (
                          <Button size="small" startIcon={<AddIcon sx={{ fontSize: '0.9rem' }} />} onClick={() => onAddDatabaseDisk(db.id)} sx={{ minWidth: 'auto', px: 1, py: 0.25, fontSize: '0.75rem', textTransform: 'none' }}>
                            Add
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={SPACER_CELL_SX}></TableCell>
                    <TableCell sx={{ ...TABLE_CELL_SX, ...CELL_W(COL.QTY), textAlign: 'center' }}>
                      <TextField type="number" size="small" value={db.quantity} onChange={(e) => onUpdateDatabase(db.id, 'quantity', parseInt(e.target.value) || 1)} inputProps={{ min: 1, style: { textAlign: 'center' } }} fullWidth sx={NO_SPIN_SX} />
                    </TableCell>
                    <TableCell sx={{ ...TABLE_CELL_SX, ...CELL_W(COL.COST), textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap' }}>
                      {dbAvailable ? formatCurrency(cost) : <Typography color="error" fontWeight={700}>Unavailable</Typography>}
                    </TableCell>
                    <TableCell sx={{ ...TABLE_CELL_SX, ...CELL_W(COL.ACT), textAlign: 'right' }}>
                      <IconButton size="small" color="error" onClick={() => onDeleteDatabase(db.id)}><DeleteIcon /></IconButton>
                    </TableCell>
                  </TableRow>
                );
              }

              // PaaS DB row
              const coreOptions: SelectOption[] = [...new Set(filteredDatabaseConfigs.filter((dc) => dc.DatabaseTypeId === parseInt(String(db.databaseTypeId), 10)).map((dc) => dc.Cores))]
                .sort((a, b) => a - b)
                .map((c) => ({ value: c, label: c }));
              const coreOptsWithCurrent = ensureCurrentOption(coreOptions, db.cores, (v) => String(v));

              const availableTiers: SelectOption[] = getAvailableTiers(db.databaseTypeId, db.cores).map((cfg) => ({ value: cfg.Tier, label: cfg.Tier, cfgId: cfg.DatabaseConfigId }));
              const tierLabel = (val: number | string) => String(val);
              const tierOptsWithCurrent = ensureCurrentOption(availableTiers, db.tier, tierLabel);

              return (
                <TableRow key={db.id} hover sx={!dbAvailable ? rowErrorSX : undefined}>
                  <TableCell sx={{ ...TABLE_CELL_SX, ...CELL_W(COL.NAME) }}>
                    <TextField select size="small" value={db.databaseTypeId} onChange={(e) => onUpdateDatabase(db.id, 'databaseTypeId', e.target.value)} fullWidth>
                      {dbTypeOptsWithCurrent.map((opt) => <MenuItem key={`type-${opt.value}`} value={opt.value} disabled={opt.disabled}>{opt.label}</MenuItem>)}
                    </TextField>
                  </TableCell>
                  <TableCell sx={{ ...TABLE_CELL_SX, ...CELL_W(COL.ENV), textAlign: 'center' }}><Typography variant="caption" color="text.secondary">&mdash;</Typography></TableCell>
                  <TableCell sx={{ ...TABLE_CELL_SX, ...CELL_W(COL.CPU) }}>
                    <TextField select size="small" value={db.cores ?? ''} onChange={(e) => onUpdateDatabase(db.id, 'cores', Number(e.target.value))} disabled={!db.databaseTypeId} fullWidth>
                      {coreOptsWithCurrent.map((opt) => <MenuItem key={`dbc-${opt.value}`} value={opt.value} disabled={opt.disabled}>{opt.label}</MenuItem>)}
                    </TextField>
                  </TableCell>
                  <TableCell sx={{ ...TABLE_CELL_SX, ...CELL_W(COL.RAM), textAlign: 'center' }}><Typography variant="caption" color="text.secondary">&mdash;</Typography></TableCell>
                  <TableCell sx={{ ...TABLE_CELL_SX, ...CELL_W(COL.OS) }}>
                    <TextField select size="small" value={db.tier ?? ''} onChange={(e) => onUpdateDatabase(db.id, 'tier', e.target.value)} disabled={!db.cores} fullWidth>
                      {tierOptsWithCurrent.map((opt) => <MenuItem key={`dbt-${opt.value}`} value={opt.value} disabled={opt.disabled}>{opt.label}</MenuItem>)}
                    </TextField>
                  </TableCell>
                  <TableCell sx={{ ...TABLE_CELL_SX, ...CELL_W(COL.BACKUPS), textAlign: 'center' }}>
                    <TextField select size="small" value={(db as PaasDatabaseRow).dailyBackups ?? 0} onChange={(e) => onUpdateDatabase(db.id, 'dailyBackups', parseInt(e.target.value, 10))} sx={{ width: 80 }}>
                      {BACKUP_OPTIONS.map((opt) => <MenuItem key={`pbk-${opt.value}`} value={opt.value}>{opt.label}</MenuItem>)}
                    </TextField>
                  </TableCell>
                  <TableCell sx={{ ...TABLE_CELL_SX, minWidth: COL.STORAGE, textAlign: 'center' }}>
                    <TextField type="number" size="small" value={(db as PaasDatabaseRow).extraStorageGB ?? 0}
                      onChange={(e) => onUpdateDatabase(db.id, 'extraStorageGB', parseInt(e.target.value) || 0)}
                      InputProps={{ endAdornment: <InputAdornment position="end">GB</InputAdornment> }}
                      inputProps={{ min: 0, style: { textAlign: 'center' } }} sx={{ width: 110, ...NO_SPIN_SX }} />
                  </TableCell>
                  <TableCell sx={SPACER_CELL_SX}></TableCell>
                  <TableCell sx={{ ...TABLE_CELL_SX, ...CELL_W(COL.QTY), textAlign: 'center' }}>
                    <TextField type="number" size="small" value={db.quantity} onChange={(e) => onUpdateDatabase(db.id, 'quantity', parseInt(e.target.value) || 1)} inputProps={{ min: 1, style: { textAlign: 'center' } }} fullWidth sx={NO_SPIN_SX} />
                  </TableCell>
                  <TableCell sx={{ ...TABLE_CELL_SX, ...CELL_W(COL.COST), textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap' }}>
                    {dbAvailable ? formatCurrency(cost) : <Typography color="error" fontWeight={700}>Unavailable</Typography>}
                  </TableCell>
                  <TableCell sx={{ ...TABLE_CELL_SX, ...CELL_W(COL.ACT), textAlign: 'right' }}>
                    <IconButton size="small" color="error" onClick={() => onDeleteDatabase(db.id)}><DeleteIcon /></IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default DatabasesTable;
